/**
 * Admin Users Management Hooks
 * React hooks for managing admin users
 */

import { useCallback } from 'react';
import { useQuery, useMutation } from './useBaseHooks';
import { adminApiClient } from './adminApiClient';
import { UserDTO } from './adminTypes';
import { ApiResponse, PagedData } from './types';

/**
 * Hook to fetch paginated list of admin users
 * @param params - Query parameters (page, size, sort, search, status, role)
 * @param options - Hook options
 */
export function useAdminUsers(
  params: {
    page?: number;
    size?: number;
    sort?: string[];
    search?: string;
    status?: string;
    role?: string;
  } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<UserDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    // Build pageable object - API expects flat query params
    queryParams.page = params.page ?? 0;
    queryParams.size = params.size ?? 10;
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.status) {
      queryParams.status = params.status;
    }

    if (params.role) {
      queryParams.role = params.role;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<UserDTO>>>(
      '/api/admin/list',
      queryParams
    );

    // Handle Response wrapper
    if (response && response.data) {
      return response.data;
    }

    // If response.data is already PagedData
    if (response && typeof response === 'object' && 'content' in response) {
      return response as unknown as PagedData<UserDTO>;
    }

    // Fallback: return empty PagedData
    return {
      content: [],
      page: {
        size: params.size ?? 10,
        number: params.page ?? 0,
        totalElements: 0,
        totalPages: 0,
      },
      first: true,
      last: true,
      empty: true,
    };
  }, [params.page, params.size, params.sort, params.search, params.status, params.role]);

  return useQuery<PagedData<UserDTO>>(
    queryFn,
    {
      enabled,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );
}

/**
 * Hook to fetch a single admin user by ID
 * @param id - User ID
 * @param options - Hook options
 */
export function useAdminUser(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: UserDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const { enabled = true, onSuccess, onError } = options;

  const queryFn = useCallback(async () => {
    const response = await adminApiClient.get<ApiResponse<UserDTO>>(
      `/api/admin/${id}`
    );

    // Handle Response wrapper
    if (response && response.data) {
      return response.data;
    }

    return response as UserDTO;
  }, [id]);

  return useQuery<UserDTO>(
    queryFn,
    {
      enabled: enabled && !!id,
      onSuccess: (data) => {
        onSuccess?.(data);
      },
      onError: (error) => {
        onError?.(error);
      },
    }
  );
}

/**
 * Hook to create a new admin user
 * @param options - Hook options
 */
export function useCreateAdminUser(
  options: {
    onSuccess?: (data: ApiResponse<UserDTO>, variables: UserDTO) => void;
    onError?: (error: Error, variables: UserDTO) => void;
  } = {}
) {
  return useMutation<ApiResponse<UserDTO>, UserDTO>(
    async (userData: UserDTO) => {
      const response = await adminApiClient.post<ApiResponse<UserDTO>>(
        '/api/admin',
        userData
      );
      return response;
    },
    {
      onSuccess: options.onSuccess,
      onError: options.onError,
    }
  );
}

/**
 * Hook to update an existing admin user
 * @param options - Hook options
 */
export function useUpdateAdminUser(
  options: {
    onSuccess?: (data: ApiResponse<UserDTO>, variables: { id: number; data: UserDTO }) => void;
    onError?: (error: Error, variables: { id: number; data: UserDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<UserDTO>, { id: number; data: UserDTO }>(
    async ({ id, data }: { id: number; data: UserDTO }) => {
      const response = await adminApiClient.put<ApiResponse<UserDTO>>(
        `/api/admin/${id}`,
        data
      );
      return response;
    },
    {
      onSuccess: options.onSuccess,
      onError: options.onError,
    }
  );
}

/**
 * Hook to delete an admin user
 * @param options - Hook options
 */
export function useDeleteAdminUser(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: DeleteUserVariables) => void;
    onError?: (error: Error, variables: DeleteUserVariables) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, DeleteUserVariables>(
    async (variables: DeleteUserVariables) => {
      // First delete the user
      const response = await adminApiClient.delete<ApiResponse<void>>(
        `/api/admin/${variables.userId}`
      );

      // Then try to delete corresponding driver if email is provided
      if (variables.email) {
        try {
          // Try to find and delete driver with this email
          const driversResponse = await adminApiClient.get<any>(`/drivers?email=${encodeURIComponent(variables.email)}`);
          if (driversResponse.data?.content?.length > 0) {
            const driver = driversResponse.data.content[0];
            if (driver.driverId) {
              await adminApiClient.delete<ApiResponse<void>>(`/drivers/${driver.driverId}`);
            }
          }
        } catch (driverError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding driver:', driverError);
        }
      }

      // Also try to delete corresponding rider if email is provided
      if (variables.email) {
        try {
          // Try to find and delete rider with this email
          const ridersResponse = await adminApiClient.get<any>(`/riders?email=${encodeURIComponent(variables.email)}`);
          if (ridersResponse.data?.content?.length > 0) {
            const rider = ridersResponse.data.content[0];
            if (rider.riderId) {
              await adminApiClient.delete<ApiResponse<void>>(`/riders/${rider.riderId}`);
            }
          }
        } catch (riderError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding rider:', riderError);
        }
      }

      return response;
    },
    {
      onSuccess: options.onSuccess,
      onError: options.onError,
    }
  );
}

/**
 * Variables for delete user operation
 */
export interface DeleteUserVariables {
  userId: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Hook to activate an admin user
 * @param options - Hook options
 */
export function useActivateAdminUser(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, number>(
    async (id: number) => {
      const response = await adminApiClient.delete<ApiResponse<void>>(
        `/api/admin/active/${id}`
      );
      return response;
    },
    {
      onSuccess: options.onSuccess,
      onError: options.onError,
    }
  );
}

/**
 * Hook to deactivate an admin user
 * @param options - Hook options
 */
export function useDeactivateAdminUser(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, number>(
    async (id: number) => {
      const response = await adminApiClient.delete<ApiResponse<void>>(
        `/api/admin/inactive/${id}`
      );
      return response;
    },
    {
      onSuccess: options.onSuccess,
      onError: options.onError,
    }
  );
}

